import { apiDelete, apiGet, apiGetMeta, apiPatch, apiPost } from './api-client.js';
import type { OffsetPageMeta } from './files.js';

/**
 * Typed wrappers over API_CONTRACT.md §13.10's user/role/session administration
 * (`api/src/modules/identity/transport/user-admin-routes.ts`, read directly — shapes
 * match that file's `userPayload`/`userListPayload`/`assignmentPayload`/`sessionPayload`
 * exactly, not the contract's prose table). This is the ONLY file that knows these
 * paths, matching `lib/files.ts`'s own convention.
 */

export interface AdminOrgUnitRef {
  code: string;
  nameNe: string;
  nameEn: string;
}

export type UserAccountStatus = 'active' | 'suspended' | 'deactivated';

export interface AdminUser {
  id: string;
  username: string;
  fullName: string;
  email: string | null;
  status: UserAccountStatus;
  locale: 'ne' | 'en';
  mustChangePassword: boolean;
  mfaEnabled: boolean;
  lockedUntil: string | null;
  primaryOrgUnitId: string | null;
  designationId: string | null;
  lastLoginAt: string | null;
  version: number;
}

/** `GET /users` row — `AdminUser` plus the list-only projection fields. */
export interface AdminUserListRow extends AdminUser {
  orgUnit: AdminOrgUnitRef | null;
  designationCode: string | null;
  roleCodes: string[];
}

export interface RoleAssignment {
  id: string;
  userId: string;
  roleCode: string;
  scopeLevel: string;
  scopeOrgUnitId: string | null;
  scopeOrgUnitCode: string | null;
  maxClearanceRank: number;
  validFrom: string;
  validTo: string | null;
  live: boolean;
}

/** `GET /users/{id}` — the profile plus role assignments and a session count "in context" (I19). */
export interface AdminUserDetail extends AdminUser {
  roleAssignments: RoleAssignment[];
  sessionCount: number;
}

export interface AdminSession {
  id: string;
  issuedAt: string;
  lastSeenAt: string;
  idleExpiresAt: string;
  absoluteExpiresAt: string;
  mfaSatisfied: boolean;
  /**
   * `sessionPayload` (`user-admin-routes.ts`) passes `SessionSummary.ip`/`.userAgent`
   * straight through, and the repository types both `string | null` (a session created
   * without a captured client IP or `User-Agent` header) — declared non-nullable here
   * until this pass, which made the IP column render `null`/nothing for such a row
   * instead of a visible "not recorded" fallback (`UserSessionsPage.tsx`'s `ip` column).
   */
  ip: string | null;
  userAgent: string | null;
  /** True when this row is the CALLER's own current session (irrelevant when viewing another user's list). */
  current: boolean;
}

export interface RoleDescriptor {
  code: string;
  labelNe: string;
  labelEn: string;
  defaultScopeLevel: string | null;
  requiresMfa: boolean;
  assignable: boolean;
}

export interface ClearanceLevelOption {
  code: string;
  rank: number;
  labelNe: string;
  labelEn: string;
}

/**
 * `GET /roles` — the seeded vocabulary AND which of it THIS caller may grant
 * (`grantable`). The grant form's role picker renders `grantable` only — offering a
 * role the caller will then be refused for is worse than offering fewer
 * (`grant-authority.ts`'s own comment on `rolesGrantableBy`).
 */
export interface RoleCatalogue {
  roles: RoleDescriptor[];
  grantable: string[];
  clearanceLevels: ClearanceLevelOption[];
}

export function getRoleCatalogue(signal?: AbortSignal): Promise<RoleCatalogue> {
  return apiGet<RoleCatalogue>('/roles', signal);
}

export interface ListUsersParams {
  q?: string;
  status?: UserAccountStatus;
  orgUnitId?: string;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export async function listUsers(
  params: ListUsersParams = {},
  signal?: AbortSignal,
): Promise<{ rows: AdminUserListRow[]; page: OffsetPageMeta }> {
  const query = buildQuery({
    q: params.q,
    status: params.status,
    orgUnitId: params.orgUnitId,
    page: params.page,
    pageSize: params.pageSize,
  });
  const { data, meta } = await apiGetMeta<AdminUserListRow[]>(`/users${query}`, signal);
  const page = (meta?.page as OffsetPageMeta | undefined) ?? {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? data.length,
    totalItems: data.length,
    totalPages: 1,
  };
  return { rows: data, page };
}

export interface CreateUserInput {
  username: string;
  fullName: string;
  password: string;
  email?: string | null;
  phone?: string | null;
  locale?: 'ne' | 'en';
  primaryOrgUnitId?: string | null;
  designationId?: string | null;
}

export function createUser(input: CreateUserInput, signal?: AbortSignal): Promise<AdminUser> {
  return apiPost<AdminUser>('/users', input, signal);
}

export function getUser(userId: string, signal?: AbortSignal): Promise<AdminUserDetail> {
  return apiGet<AdminUserDetail>(`/users/${userId}`, signal);
}

export interface UpdateUserInput {
  expectedVersion: number;
  fullName?: string;
  email?: string | null;
  phone?: string | null;
  locale?: 'ne' | 'en';
  primaryOrgUnitId?: string | null;
  designationId?: string | null;
}

export function updateUser(userId: string, input: UpdateUserInput, signal?: AbortSignal): Promise<AdminUser> {
  return apiPatch<AdminUser>(`/users/${userId}`, input, signal);
}

export interface DeactivateUserResult {
  userId: string;
  sessionsRemoved: number;
  sessionsRemaining: number;
}

export function deactivateUser(userId: string, reason: string, signal?: AbortSignal): Promise<DeactivateUserResult> {
  return apiPost<DeactivateUserResult>(`/users/${userId}/deactivate`, { reason }, signal);
}

export function listRoleAssignments(userId: string, signal?: AbortSignal): Promise<RoleAssignment[]> {
  return apiGet<RoleAssignment[]>(`/users/${userId}/role-assignments`, signal);
}

/**
 * `POST /users/{id}/role-assignments` — API_CONTRACT §13.10 amendment 4, THE privilege
 * boundary. `scopeOrgUnitId` is required unless `scopeLevel` is anchorless (`own`,
 * `platform`) — enforced client-side in the form BEFORE this is ever called
 * (R-044/`scope-anchor-rule.test.ts`'s rule), and re-enforced server-side regardless.
 */
export interface AssignRoleInput {
  roleCode: string;
  scopeLevel: string;
  scopeOrgUnitId?: string | null;
  maxClearanceRank: number;
  validTo?: string | null;
}

export interface AssignRoleResult {
  id: string;
  userId: string;
  roleCode: string;
  scopeLevel: string;
  mfaEnrolment: { token: string; expiresAt: string } | null;
}

export function assignRole(userId: string, input: AssignRoleInput, signal?: AbortSignal): Promise<AssignRoleResult> {
  return apiPost<AssignRoleResult>(`/users/${userId}/role-assignments`, input, signal);
}

export function revokeRoleAssignment(
  userId: string,
  assignmentId: string,
  signal?: AbortSignal,
): Promise<{ id: string; effective: string }> {
  return apiDelete<{ id: string; effective: string }>(`/users/${userId}/role-assignments/${assignmentId}`, signal);
}

export function getUserSessions(userId: string, signal?: AbortSignal): Promise<AdminSession[]> {
  return apiGet<AdminSession[]>(`/users/${userId}/sessions`, signal);
}

export function revokeSession(sessionId: string, signal?: AbortSignal): Promise<{ id: string; revoked: boolean }> {
  return apiDelete<{ id: string; revoked: boolean }>(`/sessions/${sessionId}`, signal);
}

export function revokeAllUserSessions(
  userId: string,
  reason: string,
  signal?: AbortSignal,
): Promise<{ userId: string; sessionsRemoved: number }> {
  return apiPost<{ userId: string; sessionsRemoved: number }>(`/users/${userId}/sessions/revoke-all`, { reason }, signal);
}

/**
 * The five `error.details.refusal` codes `grant-authority.ts` can return on
 * grant/revoke, mirrored client-side ONLY as a string union for typing — the actual
 * refusal always arrives on the wire as `error.details.refusal`, never re-derived here.
 */
export type GrantRefusalCode =
  | 'NO_GRANT_AUTHORITY'
  | 'SELF_GRANT'
  | 'ROLE_ABOVE_ACTOR'
  | 'SCOPE_ABOVE_ACTOR'
  | 'SCOPE_OUTSIDE_ACTOR'
  | 'CLEARANCE_ABOVE_ACTOR';

/** R-044's rule, mirrored client-side so the form enforces it before submit, not after a 422. */
export const SCOPE_LEVELS = ['own', 'section', 'division', 'ministry', 'platform'] as const;
export type ScopeLevelOption = (typeof SCOPE_LEVELS)[number];
const ANCHORLESS_SCOPE_LEVELS: readonly string[] = ['own', 'platform'];

export function scopeRequiresAnchor(scopeLevel: string): boolean {
  return !ANCHORLESS_SCOPE_LEVELS.includes(scopeLevel);
}

export { ApiError, NetworkError } from './api-client.js';
